import type {
  AppConfig,
  DocLink,
  DownloadsConfig,
  EditionConfig,
  LocaleString,
  RuntimeConfig,
  WizardConfig,
} from "@/types";
import { logger } from "@/logging";

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(path, { cache: "no-cache" });
  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.status}`);
  }
  return (await response.json()) as T;
}

function isAppConfig(value: unknown): value is AppConfig {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<AppConfig>;
  return (
    typeof candidate.schemaVersion === "string" &&
    typeof candidate.edition === "string" &&
    Array.isArray(candidate.supportedLocales)
  );
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const started = performance.now();
  const app = await fetchJson<AppConfig>("/config/app.json");

  if (!isAppConfig(app)) {
    throw new Error("Invalid app.json schema");
  }

  const base = `/config/editions/${app.edition}`;
  const [wizard, downloads, localeStrings, docLinks] = await Promise.all([
    fetchJson<WizardConfig>(`${base}/wizard.json`),
    fetchJson<DownloadsConfig>(`${base}/downloads.json`),
    fetchJson<{ strings: LocaleString[] }>(`${base}/locale-strings.json`),
    fetchJson<{ links: DocLink[] }>(`${base}/doc-links.json`),
  ]);

  const edition: EditionConfig = {
    wizard,
    downloads,
    localeStrings: localeStrings.strings,
    docLinks: docLinks.links,
  };

  logger.info({
    message: "Loaded runtime configuration",
    edition: app.edition,
    appVersion: app.appVersion,
    schemaVersion: app.schemaVersion,
    durationMs: Math.round(performance.now() - started),
  });

  return { app, edition };
}

export function resolveLocaleString(
  strings: LocaleString[],
  key: string,
  locale: string,
  fallback: string,
): string {
  const match = strings.find((item) => item.key === key);
  if (!match) return fallback;
  const translation = match.translations.find((item) => item.locale === locale);
  if (translation?.value) return translation.value;
  return match.englishValue || fallback;
}

export function resolveDocLink(
  links: DocLink[],
  refNumber: number,
  locale: string,
  fallback: string,
): string {
  const match = links.find((item) => item.refNumber === refNumber);
  if (!match) return fallback;
  const translation = match.translations.find((item) => item.locale === locale);
  return translation?.url || match.englishUrl || fallback;
}

export function isNordicLocale(locale: string): boolean {
  return locale === "da" || locale === "sv" || locale === "no";
}
